import { ReactNode } from 'react';
import { lt, useDialogAction } from '@mews/framework';
import { Button, Dialog, DialogProps, Stack } from '@optimus-web/core';

interface Params extends Pick<DialogProps, 'loading' | 'title'> {
    confirmButtonDataTestId?: string;
    deleteAction: () => Promise<unknown>;
    renderContent: () => ReactNode;
    primaryButtonTextKey?: Mews.TextKey;
}

export const useDeleteConfirmationDialog = ({
    confirmButtonDataTestId,
    deleteAction,
    loading,
    renderContent,
    title,
    primaryButtonTextKey,
}: Params) => {
    const action = useDialogAction(deleteAction);

    const confirmationDialog = (
        <Dialog
            title={title}
            active={action.states.isDialogOpened}
            loading={loading || action.states.isProcessing}
            onClose={action.handlers.handleCancel}
            modal
            footer={{
                primary: (
                    <Button
                        variant="destructive"
                        onClick={action.handlers.handleConfirm}
                        type="submit"
                        size="medium"
                        data-test-id={confirmButtonDataTestId}
                    >
                        {lt(primaryButtonTextKey ?? 'Delete')}
                    </Button>
                ),
                secondary: (
                    <Button variant="tertiary" onClick={action.handlers.handleCancel} type="button" size="medium">
                        {lt('Cancel')}
                    </Button>
                ),
            }}
        >
            <Stack vertical spacing="200">
                {renderContent()}
            </Stack>
        </Dialog>
    );

    return { confirmationDialog, openConfirmationDialog: action.handlers.handleOpen };
};
